import { fetchUtils } from 'react-admin'

import env from '../config/env'
import { errorParser } from '../config/errors'
import { serializeFile } from '../utils'

export default (baseDataProvider, apiProvider) => {
  const uploadPicture = (resource, picture_data) =>
    serializeFile(picture_data.rawFile)
      .then((serializedFile) =>
        errorParser(apiProvider)(`/pictures/${resource}`, {
          method: 'POST',
          body: JSON.stringify(serializedFile),
        }),
      )
      .then(({ json }) => json)

  const uploadVehicleInformationDocument = (information_document_data) => {
    const formData = new FormData()
    information_document_data.title && formData.append('title', information_document_data.title)
    information_document_data.content && formData.append('content', information_document_data.content)
    formData.append('file', information_document_data.rawFile)
    return errorParser(apiProvider)('/vehicles/documents', {
      method: 'POST',
      body: formData,
    }).then(({ json }) => json)
  }

  const processData = async (resource, data) => {
    const { picture_data, information_document_data, ...restData } = data

    if (resource === 'incident-reports' && picture_data) {
      return { ...restData, picture: (await serializeFile(picture_data.rawFile)).data }
    }

    const [picture, information_document] = await Promise.all([
      picture_data?.rawFile ? uploadPicture(resource, picture_data).then(({ url }) => url) : picture_data?.src,
      information_document_data?.rawFile
        ? uploadVehicleInformationDocument(information_document_data).then(({ url }) => url)
        : information_document_data?.src,
    ])

    return { ...restData, picture, information_document }
  }

  // Decorate all provider methods with errorParser
  const base = Object.fromEntries(Object.entries(baseDataProvider).map(([name, func]) => [name, errorParser(func)]))

  const sanitizeFilterParams = (filters) => {
    return Object.fromEntries(Object.entries(filters).filter(([key, value]) => value !== ''))
  }

  const blobToDataUrl = async (blob, mimeType = 'image/jpeg') => {
    const bytes = new Uint8Array(await blob.arrayBuffer())
    let binary = ''

    for (let index = 0; index < bytes.length; index += 0x8000) {
      binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000))
    }

    return `data:${mimeType};base64,${btoa(binary)}`
  }

  const getDocumentImageSrc = async (response) => {
    if (typeof Response !== 'undefined' && response instanceof Response) {
      const blob = await response.blob()
      return blobToDataUrl(blob, blob.type || undefined)
    }

    if (typeof Blob !== 'undefined' && response instanceof Blob) {
      return blobToDataUrl(response, response.type || undefined)
    }

    const imageData = response.json ?? response.body
    const base64 = imageData?.content ?? imageData?.data ?? imageData?.base64 ?? imageData
    const fallbackMimeType = imageData?.mime_type ?? imageData?.mimeType ?? imageData?.['mime-type'] ?? 'image/jpeg'

    return base64?.startsWith('data:') ? base64 : `data:${fallbackMimeType};base64,${base64}`
  }

  return {
    ...base,
    // Return deleted record after delete
    delete: (resource, params) => base.delete(resource, params).then(() => ({ data: params })),
    create: async (resource, params) => {
      const processedData = await processData(resource, params.data)
      return base.create(resource, { ...params, data: processedData })
    },
    update: async (resource, params) => {
      const processedData = await processData(resource, params.data)
      return base.update(resource, { ...params, data: processedData })
    },
    getDocumentImage: async (imageId, { headers } = {}) => {
      const response = await errorParser(apiProvider)(`/user-documents/image/${imageId}`, {
        headers: new Headers(headers ?? {}),
        rawResponse: true,
      })

      return {
        data: {
          id: imageId,
          src: await getDocumentImageSrc(response),
        },
      }
    },
    getList: (resource, { filter, meta, ...params }) => {
      const sanitizedFilters = sanitizeFilterParams(filter)
      const finalResource = meta === 'export' ? `${resource}/export` : resource
      return base.getList(finalResource, { ...params, filter: sanitizedFilters })
    },
    getAvailableLoginMethods: async (email) => {
      const { json } = await fetchUtils.fetchJson(
        env.API_URL + '/login-methods' + `?email=${encodeURIComponent(email)}`,
      )
      return json?.map((method) => method.toLowerCase())
    },
    markAnnouncementsSeen: (slugs) =>
      errorParser(apiProvider)('/announcements/seen', {
        method: 'POST',
        body: JSON.stringify({ slugs }),
      }),
    action: async (resource, id, action) => {
      return await fetchUtils.fetchJson(env.API_URL + `/${resource}/${id}/actions`, {
        method: 'POST',
        body: JSON.stringify({ action }),
      })
    },
    export: async ({ resource, filter }) => {
      const sanitizedFilters = sanitizeFilterParams(filter)
      return base.create(`${resource}/export`, { data: { filter: sanitizedFilters } })
    },
  }
}
